import React, { useState, useRef, useEffect } from "react"
import { Form, message } from "antd"
import InputField from "../Common/InputField"
import { useDispatch, useSelector } from "react-redux"
import "../../styles/Components/mydetails.css"
import { authApi } from "../../Apis/authApis"
import { fetchUserDetails } from "../../ReduxToolKit/Slice/UserSlice"
import { translations } from "../../utils/Translation/staticTranslation"
import ProfilePictureModal from "../Common/Modals/ProfilePictureModal"

const MyDetails = () => {
  const user = useSelector((state) => state.user.user)

  const [form] = Form.useForm()
  const dispatch = useDispatch()

  const [editDetails, setEditDetails] = useState(true)
  const [imageLoading, setImageLoading] = useState(true)
  const [profileImage, setProfileImage] = useState(
    user?.profile_picture_url || null
  )
  const [profileImagePayload, setProfileImageUrlPayload] = useState()

  // State for modal
  const [isModalOpen, setIsModalOpen] = useState(false)
  // Add a manual override flag for deleted profile image
  const [profileDeleted, setProfileDeleted] = useState(false)
  // Add loading state for profile picture operations
  const [isProfileUpdateLoading, setIsProfileUpdateLoading] = useState(false)

  const lang = localStorage.getItem("language") || "ar"
  const settingPage = translations[lang]?.settingPage
  const common_btn = translations[lang]?.common_btn

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      setProfileImageUrlPayload(file)
      setProfileImage(URL.createObjectURL(file))
      // Reset profile deleted flag when uploading a new image
      setProfileDeleted(false)
      // Start loading
      setIsProfileUpdateLoading(true)
      handleUpdateProfile("", file)
    }
  }

  const fileInputRef = useRef(null)

  const triggerFileInput = () => {
    fileInputRef.current.click()
  }

  const onDeleteImage = () => {
    // Set loading state
    setIsProfileUpdateLoading(true)

    // Set the profile picture URL to an empty string when removing
    const formData = new FormData()
    formData.append("profile_picture_url", "") // Explicitly set profile_picture_url to empty string
    formData.append("profile_picture", "")

    try {
      authApi
        .updateUserProfile(formData)
        .then((response) => {
          if (response.status === 200) {
            message.success(settingPage?.profile_update_successfully)
            // Set the profile deleted flag
            setProfileDeleted(true)
            // Update local state to show initials instead of profile picture
            setProfileImage(null)
            // Also refresh user data from server
            dispatch(fetchUserDetails())
            // End loading and close modal
            setIsProfileUpdateLoading(false)
            setIsModalOpen(false)
          }
        })
        .catch((error) => {
          message.error(settingPage?.failed_upload_details)
          console.log("Failed to update profile", error)
          setIsProfileUpdateLoading(false)
        })
    } catch (error) {
      message.error(settingPage?.failed_upload_details)
      console.log("Failed to update profile", error)
      setIsProfileUpdateLoading(false)
    }
  }

  const handleUpdateProfile = async (values = null, file) => {
    const formData = new FormData()

    // Append profile image if available
    if (file) {
      formData.append("profile_picture", file)
    } else if (file === "") {
      // Empty string case for deletion
      formData.append("profile_picture", "")
    }

    // Append user details if values exist (i.e., updating profile details)
    if (values) {
      formData.append("name_english", values.name_english)
      formData.append("phone", values.phone)
    }

    try {
      const response = await authApi.updateUserProfile(formData)
      if (response.status === 200) {
        message.success(settingPage?.profile_update_successfully)
        if (file === "") {
          setProfileImage(null)
          setProfileDeleted(true)
        }
        // Always refresh user details after successful update
        dispatch(fetchUserDetails())

        // End loading and close modal if this was a profile picture update
        if (file) {
          setIsProfileUpdateLoading(false)
          setIsModalOpen(false)
        }
      }
    } catch (error) {
      message.error(settingPage?.failed_upload_details)
      console.log("Failed to update profile", error)
      setIsProfileUpdateLoading(false)
    }
  }

  // Handle form submission
  const onFinish = (values) => {
    handleUpdateProfile(values)
    setEditDetails(true) // Switch back to view mode after submission
  }

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        name_english:
          lang === "ar" ? user?.name_arabic : user?.name_english || "",
        email: user.email || "",
        phone: user.phone || "",
      })

      // Only update profile image from user data if it hasn't been manually deleted
      if (!profileDeleted) {
        setProfileImage(user?.profile_picture_url || null)
      }
    }
  }, [user, form, profileDeleted])

  const handleOpenModal = () => {
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    // Don't allow closing the modal during loading
    if (!isProfileUpdateLoading) {
      setIsModalOpen(false)
    }
  }

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user?.name_english) return ""
    const nameArray = user.name_english.split(" ")
    return `${nameArray[0]?.charAt(0) || ""}${nameArray[1]?.charAt(0) || ""}`
  }

  // Determine if we should show the profile image or not
  const shouldShowProfileImage = () => {
    // If profile is deleted, don't show image even if URL exists in user object
    if (profileDeleted) return false
    return !!profileImage
  }

  // Cancel edit mode and reset form values
  const handleCancelEdit = () => {
    form.setFieldsValue({
      name_english:
        lang === "ar" ? user?.name_arabic : user?.name_english || "",
      email: user?.email || "",
      phone: user?.phone || "",
    })
    setEditDetails(true)
  }

  return (
    <div className="d-flex flex-column flex-grow-1">
      {/* Profile Picture Modal */}
      <ProfilePictureModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        profileImage={profileImage}
        onRemove={onDeleteImage}
        onChangeClick={triggerFileInput}
        lang={lang}
        isLoading={isProfileUpdateLoading}
        common_btn={{
          ...common_btn,
          cancel: common_btn?.cancel || "Cancel",
        }}
        userInitials={getUserInitials()}
      />

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImageChange}
        style={{ display: "none" }}
        accept="image/*"
      />

      <div className="setting-email-pic">
        <div className="d-flex align-items-center">
          <div
            className={`relative name-box ${
              lang === "ar" ? "ml-14" : "mr-14"
            } `}
          >
            {/* Profile image or initials */}
            <div className="profile-image-container">
              {imageLoading && shouldShowProfileImage() && (
                <div className="shimmer-effect"></div> // Shimmer effect while loading
              )}

              {shouldShowProfileImage() ? (
                <img
                  src={profileImage}
                  alt="Profile"
                  className="profile-image"
                  style={{
                    width: "62px",
                    borderRadius: "6px",
                    height: "100%",
                    objectFit: "cover",
                  }}
                  onLoad={() => setImageLoading(false)} // Hide shimmer when loaded
                />
              ) : (
                <div className="d-flex align-items-center justify-content-center w-100 h-100">
                  <p className="default-initials mb-0">{getUserInitials()}</p>
                </div>
              )}
            </div>

            {/* Edit icon */}
            <div
              onClick={handleOpenModal}
              className="absolute edit-pic-icon d-flex justify-content-center align-items-center"
              style={{
                bottom: "-8px",
                right: "-10px",
                cursor: "pointer",
                zIndex: 11,
              }}
            >
              <img
                src="/icons/editPicIcon.svg"
                height={12}
                width={12}
                alt="Edit"
              />
            </div>
          </div>

          {/* User info */}
          <div>
            <p className="text-18 text-1F1F1F text-600 mb-0">
              {lang === "ar" ? user?.name_arabic : user?.name_english}
            </p>
            <p className="text-A1A1A1 text-14 mb-0">{user?.email}</p>
          </div>
        </div>
      </div>

      <div className="personal-info">
        <div
          className="d-flex justify-content-between mb26"
          style={{ height: "44px" }}
        >
          <p className="text-16 text-1F1F1F font-600  mb-0 ">
            {settingPage?.personal_information}
          </p>
          {editDetails && (
            <div
              className="edit-icon"
              onClick={() => setEditDetails(!editDetails)}
            >
              <img src="/settings/editicon.svg" alt="" />
            </div>
          )}
        </div>

        <Form
          form={form}
          layout="vertical"
          initialValues={{
            name_english:
              lang === "ar" ? user?.name_arabic : user?.name_english,
            email: user?.email || "",
            phone: user?.phone || "",
          }}
          onFinish={onFinish}
        >
          <InputField
            disabled={editDetails}
            formclass="w-100"
            label={settingPage?.full_name}
            name="name_english"
            placeholder={settingPage?.full_name_placeholder}
            className="setting-input-field"
            rules={[{ required: true, message: settingPage?.full_name_error }]}
          />
          <div className="d-flex">
            <InputField
              disabled={true}
              formclass={`w-100 mr-12 ${lang === "ar" ? "ml-12" : "mr-12"} `}
              label={settingPage?.email}
              name="email"
              placeholder={settingPage?.email_placeholder}
              className="setting-input-field"
              rules={[
                { required: true, message: settingPage?.email_error_1 },
                { type: "email", message: settingPage?.email_error_2 },
              ]}
            />
            <InputField
              disabled={editDetails}
              formclass="w-100"
              label={settingPage?.phone_number}
              name="phone"
              placeholder={settingPage?.phone_number_placeholder}
              className="setting-input-field"
              rules={[
                { required: true, message: settingPage?.phone_number_error },
                {
                  pattern: /^[+0-9]+$/,
                  message: settingPage?.phone_number_error_2,
                },
              ]}
            />
          </div>
          {!editDetails && (
            <div className="d-flex justify-content-end">
              <button
                className={`cancel-btn ${lang === "ar" ? "ml-10" : "mr-10"}`}
                type="button"
                onClick={handleCancelEdit}
              >
                {settingPage?.discard_btn}
              </button>
              <button
                className="pxy-13-26 border-none bg-504943 border-radius-59 text-FFFFFF"
                type="submit"
              >
                {common_btn?.save}
              </button>
            </div>
          )}
        </Form>
      </div>
    </div>
  )
}

export default MyDetails
